<script>
import ModalCourseSubjectComponent from '../../features/modals/ModalCourseSubjectComponent';
import ModalCourseSubjectActivityView from './ModalCourseSubjectActivityView';
import ModalCourseSubjectTestView from './ModalCourseSubjectTestView';
import draggable from 'vuedraggable';

export default {
  name: 'ModalCourseSubjectView',
  components: {
    'modal-course-subject-activity-view': ModalCourseSubjectActivityView,
    'modal-course-subject-test-view': ModalCourseSubjectTestView,
    draggable,
  },
  extends: ModalCourseSubjectComponent,
};
</script>

<template>
  <div>
    <el-dialog
      v-model="conf.isVisible"
      title="Tema"
      width="55%"
      class="course-subject-modal"
      append-to-body
      :before-close="onCancel"
      :close-on-click-modal="false"
    >
      <el-form
        ref="formData"
        v-loading="conf.isLoading"
        :model="formData"
        :rules="rules"
        status-icon
        :label-position="'top'"
      >
        <fieldset>
          <legend>Información de Tema</legend>
          <el-row
            class="row-bg"
            :gutter="20"
          >
            <el-col
              :xs="24"
              :sm="18"
              :md="18"
              :lg="20"
              :xl="20"
            >
              <el-form-item
                label="Título"
                prop="title"
              >
                <el-input
                  v-model="formData.title"
                  maxlength="120"
                  size="small"
                />
              </el-form-item>
            </el-col>
            <el-col
              :xs="24"
              :sm="6"
              :md="6"
              :lg="4"
              :xl="4"
            >
              <el-form-item
                label="Orden"
                prop="sort"
              >
                <el-input-number
                  v-model="formData.sort"
                  size="small"
                  :disabled="true"
                />
              </el-form-item>
            </el-col>
          </el-row>
          <el-row
            type="flex"
            class="row-bg"
            :gutter="20"
          >
            <el-col :span="24">
              <el-form-item
                label="Descripción"
                prop="description"
              >
                <el-input
                  v-model="formData.description"
                  type="textarea"
                  :row="3"
                  maxlength="255"
                  size="small"
                />
              </el-form-item>
            </el-col>
          </el-row>
          <div class="course-section-header">
            <div class="title-icon">
              <h3 class="title">
                <i class="uil uil-clipboard-notes" /> Actividades del Tema
              </h3>
            </div>
            <el-button-group>
              <el-button
                type="primary"
                size="small"
                class="btn-primary"
                icon="el-icon-circle-plus"
                :disabled="!onCanCreateCourseSubjectActivity(modulePermissions)"
                @click="onCreateCourseSubjectActivity"
              >
                Agregar Actividad
              </el-button>
              <el-button
                type="primary"
                size="small"
                class="btn-primary"
                icon="el-icon-circle-plus"
                :disabled="!onCanCreateCourseSubjectActivity(modulePermissions)"
                @click="onCreateCourseSubjectTest"
              >
                Agregar Examen
              </el-button>
            </el-button-group>
          </div>

          <div class="course-sections-wrapper">
            <!-- Empty State -->
            <div
              v-if="!hasVisibleCourseSubjectActivities()"
              class="course-sections-empty"
            >
              <el-empty
                :image-size="60"
                description="No hay actividades agregadas en este tema todavía."
              >
                <el-button-group>
                  <el-button
                    type="primary"
                    size="small"
                    class="btn-primary"
                    icon="el-icon-circle-plus"
                    :disabled="!onCanCreateCourseSubjectActivity(modulePermissions)"
                    @click="onCreateCourseSubjectActivity"
                  >
                    Agregar Actividad
                  </el-button>
                  <el-button
                    type="primary"
                    size="small"
                    class="btn-primary"
                    icon="el-icon-circle-plus"
                    :disabled="!onCanCreateCourseSubjectActivity(modulePermissions)"
                    @click="onCreateCourseSubjectTest"
                  >
                    Agregar Examen
                  </el-button>
                </el-button-group>
              </el-empty>
            </div>

            <!-- Activities Table -->
            <div
              v-else
              class="course-sections-table"
            >
              <div class="sections-table-head">
                <div class="th-col col-sort">ORDEN</div>
                <div class="th-col col-title">TÍTULO</div>
                <div class="th-col col-activity-type">TIPO</div>
                <div class="th-col col-status">ESTADO</div>
                <div class="th-col col-actions">OPCIONES</div>
              </div>

              <draggable
                v-model="formData.courseSubjectActivities"
                item-key="sort"
                handle=".drag-handle"
                ghost-class="ghost-section-row"
                @change="onChangeDrag"
              >
                <template #item="{ element, index }">
                  <div
                    v-show="element.updateState !== formData.UPDATE_STATUS.REMOVED"
                    :key="element.sort || index"
                    class="sections-table-row"
                  >
                    <!-- Orden / Drag Handle -->
                    <div class="td-col col-sort">
                      <i
                        class="fa fa-grip-vertical drag-handle"
                        title="Arrastrar para ordenar"
                      />
                      <span class="order-badge">{{ element.sort }}</span>
                    </div>

                    <!-- Título -->
                    <div class="td-col col-title">
                      <span class="section-title-text">{{ element.title }}</span>
                    </div>

                    <!-- Tipo de Actividad -->
                    <div class="td-col col-activity-type">
                      <el-tag
                        size="small"
                        :type="element.courseSubjectActivityType === 2 ? 'warning' : 'primary'"
                        effect="plain"
                      >
                        <i :class="element.courseSubjectActivityType === 2 ? 'uil uil-file-question-alt' : 'uil uil-play-circle'" />
                        {{ getActivityTypeName(element.courseSubjectActivityType) }}
                      </el-tag>
                    </div>

                    <!-- Estado -->
                    <div class="td-col col-status">
                      <el-tag
                        v-if="element.isPublic"
                        size="small"
                        type="success"
                        effect="plain"
                      >
                        Público
                      </el-tag>
                      <el-tag
                        v-else
                        size="small"
                        type="info"
                        effect="plain"
                      >
                        Privado
                      </el-tag>
                    </div>

                    <!-- Opciones -->
                    <div class="td-col col-actions">
                      <el-dropdown
                        trigger="click"
                        @command="onChangeItemOptions"
                      >
                        <el-button
                          size="small"
                          class="btn-row-action"
                        >
                          <i class="fa fa-ellipsis-v" />
                        </el-button>
                        <template #dropdown>
                          <el-dropdown-menu>
                            <el-dropdown-item
                              :command="{
                                command: 'edit',
                                item: element,
                                index: index,
                              }"
                              :disabled="!onCanReadCourseSubjectActivity(modulePermissions)"
                            >
                              <i
                                class="el-icon-edit"
                                aria-hidden="true"
                              />
                              Mostrar
                            </el-dropdown-item>
                            <el-dropdown-item
                              :command="{
                                command: 'delete',
                                item: element,
                                index: index,
                              }"
                              :disabled="!onCanDeleteCourseSubjectActivity(modulePermissions)"
                            >
                              <i
                                class="el-icon-delete"
                                aria-hidden="true"
                              />
                              Eliminar
                            </el-dropdown-item>
                          </el-dropdown-menu>
                        </template>
                      </el-dropdown>
                    </div>
                  </div>
                </template>
              </draggable>
            </div>
          </div>
        </fieldset>
      </el-form>
      <template #footer>
        <span class="dialog-footer">
          <el-button
            size="small"
            class="btn-cancel"
            @click="onCancel"
          >Cancelar</el-button>
          <el-button
            type="primary"
            :disabled="!onCanSaveOrUpdate()"
            class="btn-primary"
            size="small"
            @click="onSubmitForm('formData')"
          >Aceptar</el-button>
        </span>
      </template>
    </el-dialog>
    <modal-course-subject-activity-view
      v-if="modalItemCourseSubjectActivity.isVisible"
      :form-data="itemCourseSubjectActivity"
      :conf="modalItemCourseSubjectActivity"
    />
    <modal-course-subject-test-view
      v-if="modalItemCourseSubjectTest.isVisible"
      :form-data="itemCourseSubjectActivity"
      :conf="modalItemCourseSubjectTest"
    />
  </div>
</template>
